import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { recordSuccessfulCartAdd, showCartAddNotice, hideCartAddNotice } from '@theme/br-cart-add-feedback';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  #mobileSwipeQuery = window.matchMedia('(max-width: 749px)');
  /** @type {HTMLDialogElement | null} */
  #swipePanel = null;
  /** @type {{ id: number, input: 'touch' | 'pointer', x: number, y: number, width: number, dx: number, lastX: number, time: number, velocity: number, dragging: boolean } | null} */
  #swipe = null;
  #swipeResetTimer = 0;
  #suppressSwipeClick = false;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#swipePanel = this.#dialog;
    this.#swipePanel?.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
    this.#swipePanel?.addEventListener('touchmove', this.#handleTouchMove, { passive: false });
    this.#swipePanel?.addEventListener('touchend', this.#handleTouchEnd, { passive: false });
    this.#swipePanel?.addEventListener('touchcancel', this.#cancelSwipe, { passive: true });
    this.#swipePanel?.addEventListener('pointerdown', this.#handlePointerDown);
    this.#swipePanel?.addEventListener('lostpointercapture', this.#handlePointerCancel);
    this.#swipePanel?.addEventListener('dragstart', this.#handleNativeDrag);
    this.#swipePanel?.addEventListener('click', this.#handleSwipeClick, true);
    document.addEventListener('pointermove', this.#handlePointerMove, { passive: false });
    document.addEventListener('pointerup', this.#handlePointerEnd);
    document.addEventListener('pointercancel', this.#handlePointerCancel);
    window.addEventListener('blur', this.#cancelSwipe);
    this.#swipePanel?.addEventListener('close', this.#resetSwipe);
    this.#mobileSwipeQuery.addEventListener('change', this.#resetSwipe);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#swipePanel?.removeEventListener('touchstart', this.#handleTouchStart);
    this.#swipePanel?.removeEventListener('touchmove', this.#handleTouchMove);
    this.#swipePanel?.removeEventListener('touchend', this.#handleTouchEnd);
    this.#swipePanel?.removeEventListener('touchcancel', this.#cancelSwipe);
    this.#swipePanel?.removeEventListener('pointerdown', this.#handlePointerDown);
    this.#swipePanel?.removeEventListener('lostpointercapture', this.#handlePointerCancel);
    this.#swipePanel?.removeEventListener('dragstart', this.#handleNativeDrag);
    this.#swipePanel?.removeEventListener('click', this.#handleSwipeClick, true);
    document.removeEventListener('pointermove', this.#handlePointerMove);
    document.removeEventListener('pointerup', this.#handlePointerEnd);
    document.removeEventListener('pointercancel', this.#handlePointerCancel);
    window.removeEventListener('blur', this.#cancelSwipe);
    this.#swipePanel?.removeEventListener('close', this.#resetSwipe);
    this.#mobileSwipeQuery.removeEventListener('change', this.#resetSwipe);
    this.#resetSwipe();
    this.#swipePanel = null;
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    hideCartAddNotice();
    this.#suppressSwipeClick = false;
    this.#resetSwipe();
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /** @param {TouchEvent} event */
  #handleTouchStart = (event) => {
    this.#suppressSwipeClick = false;
    if (event.touches.length !== 1) {
      this.#cancelSwipe();
      return;
    }
    const touch = event.touches[0];
    if (touch) this.#startSwipe(event.target, touch.clientX, touch.clientY, touch.identifier, event.timeStamp, 'touch');
  };

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    // Touch keeps its existing non-passive move path; don't process the same gesture twice.
    if (event.pointerType === 'touch') return;
    this.#suppressSwipeClick = false;
    if (!event.isPrimary || event.button !== 0) return this.#cancelSwipe();
    this.#startSwipe(event.target, event.clientX, event.clientY, event.pointerId, event.timeStamp, 'pointer');
  };

  /**
   * @param {EventTarget | null} target
   * @param {number} x
   * @param {number} y
   * @param {number} id
   * @param {number} time
   * @param {'touch' | 'pointer'} input
   */
  #startSwipe(target, x, y, id, time, input) {
    const panel = this.#swipePanel;
    if (!this.#mobileSwipeQuery.matches || !this.#themeDrawer?.isOpen || !panel?.open) return;
    if (panel.hasAttribute('data-br-cart-swipe') || panel.classList.contains('theme-drawer__dialog--opening')) return;
    if (!(target instanceof Element) || target.closest('dialog') !== panel || panel.querySelector('dialog[open]')) return;
    // Only the horizontal cards are excluded: the Sale Picks heading and surrounding space can drag the drawer.
    if (target.closest('.br-cart-sale-picks__carousel, .br-sale-picks-skeleton, slideshow-component, quantity-selector-component, .quantity-selector, .cart-items__quantity, button, input, textarea, select, summary, [contenteditable]:not([contenteditable="false"])')) return;
    const bounds = panel.getBoundingClientRect();
    if (x < bounds.left || x > bounds.right || y < bounds.top || y > bounds.bottom) return;
    this.#swipe = { id, input, x, y, width: bounds.width, dx: 0, lastX: x, time, velocity: 0, dragging: false };
  }

  /** @param {TouchEvent} event */
  #handleTouchMove = (event) => {
    const swipe = this.#swipe;
    if (swipe?.input !== 'touch') return;
    if (event.touches.length !== 1 || !this.#themeDrawer?.isOpen) return this.#cancelSwipe();
    const touch = Array.from(event.touches).find((point) => point.identifier === swipe.id);
    if (!touch) return this.#cancelSwipe();
    this.#moveSwipe(touch.clientX, touch.clientY, event);
  };

  /** @param {PointerEvent} event */
  #handlePointerMove = (event) => {
    if (this.#swipe?.input !== 'pointer' || event.pointerId !== this.#swipe.id) return;
    if (event.buttons !== 1 || !this.#themeDrawer?.isOpen) return this.#cancelSwipe();
    this.#moveSwipe(event.clientX, event.clientY, event);
  };

  /** @param {number} x @param {number} y @param {TouchEvent | PointerEvent} event */
  #moveSwipe(x, y, event) {
    const swipe = this.#swipe;
    if (!swipe) return;
    const dx = x - swipe.x;
    const dy = y - swipe.y;
    if (!swipe.dragging) {
      // Once a vertical gesture wins, never steal it later in the same touch sequence.
      if (Math.abs(dy) > 10 && Math.abs(dy) >= Math.abs(dx)) {
        this.#swipe = null;
        return;
      }
      if (Math.abs(dx) <= 10 || Math.abs(dx) <= Math.abs(dy)) return;
      if (!event.cancelable) return this.#cancelSwipe();
      swipe.dragging = true;
      this.#swipePanel?.setAttribute('data-br-cart-swipe', 'dragging');
      if (swipe.input === 'pointer') {
        // Capture only once a drag wins, so ordinary link taps keep their original target.
        try { this.#swipePanel?.setPointerCapture(swipe.id); } catch { /* Pointer may already have ended. */ }
      }
    }
    if (!event.cancelable) return this.#cancelSwipe();
    event.preventDefault();
    const elapsed = event.timeStamp - swipe.time;
    if (elapsed > 0) swipe.velocity = (x - swipe.lastX) / elapsed;
    swipe.lastX = x;
    swipe.time = event.timeStamp;
    swipe.dx = Math.max(0, dx);
    this.#swipePanel?.style.setProperty('--br-cart-drag-x', `${swipe.dx}px`);
    this.#swipePanel?.style.setProperty('--br-cart-backdrop-opacity', String(1 - Math.min(1, swipe.dx / swipe.width)));
  }

  /** @param {TouchEvent} event */
  #handleTouchEnd = (event) => {
    const swipe = this.#swipe;
    if (swipe?.input !== 'touch') return;
    if (event.touches.length) return this.#cancelSwipe();
    const touch = Array.from(event.changedTouches).find((point) => point.identifier === swipe.id);
    if (!touch) return this.#cancelSwipe();
    this.#finishSwipe(touch.clientX, event);
  };

  /** @param {PointerEvent} event */
  #handlePointerEnd = (event) => {
    if (this.#swipe?.input !== 'pointer' || event.pointerId !== this.#swipe.id) return;
    this.#finishSwipe(event.clientX, event);
  };

  /** @param {PointerEvent} event */
  #handlePointerCancel = (event) => {
    if (this.#swipe?.input === 'pointer' && event.pointerId === this.#swipe.id) this.#cancelSwipe();
  };

  /** @param {DragEvent} event */
  #handleNativeDrag = (event) => {
    if (this.#swipe?.input === 'pointer') event.preventDefault();
  };

  /** @param {number} x @param {TouchEvent | PointerEvent} event */
  #finishSwipe(x, event) {
    const swipe = this.#swipe;
    if (!swipe?.dragging) return this.#resetSwipe();
    if (event.cancelable) event.preventDefault();
    this.#suppressSwipeClick = true;
    // A pause before release is not a flick. Retain the last moving sample for a normal touchend at the same x.
    const elapsed = event.timeStamp - swipe.time;
    const velocity = elapsed > 80 ? 0 : x === swipe.lastX || elapsed <= 0
      ? swipe.velocity : (x - swipe.lastX) / elapsed;
    const dx = Math.max(0, x - swipe.x);
    if (dx > swipe.width * 0.3 || (dx > 0 && velocity > 0.5)) {
      this.#releaseSwipe();
      this.#swipePanel?.setAttribute('data-br-cart-swipe', 'closing');
      // Native lifecycle restores focus, unlocks scrolling and persists the closed state.
      this.#themeDrawer?.close();
    } else {
      this.#cancelSwipe();
    }
  }

  #releaseSwipe() {
    const swipe = this.#swipe;
    this.#swipe = null;
    if (swipe?.input === 'pointer' && this.#swipePanel?.hasPointerCapture(swipe.id)) {
      this.#swipePanel.releasePointerCapture(swipe.id);
    }
  }

  #cancelSwipe = () => {
    if (!this.#swipe?.dragging) return this.#resetSwipe();
    this.#releaseSwipe();
    this.#swipePanel?.setAttribute('data-br-cart-swipe', 'settling');
    this.#swipePanel?.style.setProperty('--br-cart-drag-x', '0px');
    this.#swipePanel?.style.setProperty('--br-cart-backdrop-opacity', '1');
    window.clearTimeout(this.#swipeResetTimer);
    this.#swipeResetTimer = window.setTimeout(this.#resetSwipe, 200);
  };

  #resetSwipe = () => {
    window.clearTimeout(this.#swipeResetTimer);
    this.#releaseSwipe();
    this.#swipePanel?.removeAttribute('data-br-cart-swipe');
    this.#swipePanel?.style.removeProperty('--br-cart-drag-x');
    this.#swipePanel?.style.removeProperty('--br-cart-backdrop-opacity');
  };

  /** @param {MouseEvent} event */
  #handleSwipeClick = (event) => {
    if (!this.#suppressSwipeClick || event.detail === 0) return;
    this.#suppressSwipeClick = false;
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldGiveFeedback = this.hasAttribute('auto-open') && event.action === 'add';
    if (shouldGiveFeedback) hideCartAddNotice();

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());
        if (!shouldGiveFeedback || detail?.didError !== false) {
          settle();
          return;
        }
        // Record only a confirmed success, before any asynchronous modal close.
        const firstAdd = recordSuccessfulCartAdd();
        const giveFeedback = () => {
          // Section rendering can replace this component while the request is pending.
          const drawer = /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
            document.getElementById('cart-drawer')
          );
          if (drawer && !drawer.isOpen) {
            if (firstAdd) drawer.open();
            else showCartAddNotice({
              added: this.dataset.addedLabel,
              view: this.dataset.viewCartLabel,
              close: this.dataset.closeLabel,
            });
          }
          settle();
        };
        if (sourceModal?.open) {
          sourceModal.addEventListener('close', giveFeedback, { once: true });
        } else {
          giveFeedback();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    // Keep checkout pinned while the summary's optional details scroll independently.
    dialog.setAttribute('cart-summary-sticky', 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
